/* zeus-mega.js: Zeus header menu controller (2026-10-16 rebuild; evidence in .claude/research/mega-menu-2026-10/).
   Timing from NN/g (Harley 2015; Nielsen & Li 2017) and Baymard (Scott 2023): a resting pointer opens a panel after
   300 ms (a pointer gliding across the bar opens nothing); a panel stays up until the pointer has been outside both the
   trigger and the panel for 500 ms; content appears in ~0.1 s. Once a panel is open, resting on another top-level item
   for 120 ms switches to it. Keyboard (W3C APG disclosure navigation): Enter/Space toggles, ArrowDown opens and focuses
   the first link, Escape closes and returns focus to the trigger, tabbing out closes. A mouse click on the trigger goes
   to its page (NN/g: keep each top-level choice clickable); touch, pen and keyboard toggle instead. */
(() => {
  if (window.__zeusMega) return;
  window.__zeusMega = true;

  // A pointer "rests" when it has moved < STILL_PX for STILL_MS; a panel opens once it rests AND has been on the item
  // for OPEN_DELAY (or SWITCH_DELAY when another panel is already open). A gliding pointer never opens anything.
  const OPEN_DELAY = 300, SWITCH_DELAY = 120, STILL_MS = 120, STILL_PX = 6, CLOSE_DELAY = 500, VIEW_DWELL = 140, OUT_MS = 110;
  const desktop = window.matchMedia('(min-width: 1024px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let current = null;
  let items = [];

  const backdrop = () => document.querySelector('.zm-backdrop');
  const setTop = () => {
    const header = document.querySelector('header.header');
    if (header) document.documentElement.style.setProperty('--zm-top', Math.max(0, Math.round(header.getBoundingClientRect().bottom)) + 'px');
  };
  const focusables = (root) => [...root.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')]
    .filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);

  function open(item, switching) {
    clearTimeout(item.openTimer); clearTimeout(item.closeTimer);
    if (current && current !== item) close(current, { immediate: true });
    item.token++;
    item.details.classList.remove('is-closing');
    item.details.classList.toggle('is-switching', !!switching);
    setTop();
    if (!item.details.open) item.details.open = true;
    item.summary.setAttribute('aria-expanded', 'true');
    item.summary.setAttribute('open', '');
    current = item;
    const bd = backdrop(); if (bd) bd.classList.add('is-on');
    document.body.classList.add('zm-open');
  }

  function close(item, opts = {}) {
    clearTimeout(item.openTimer); clearTimeout(item.closeTimer);
    if (!item.details.open) return;
    item.summary.setAttribute('aria-expanded', 'false');
    item.summary.removeAttribute('open');
    if (current === item) {
      current = null;
      const bd = backdrop(); if (bd) bd.classList.remove('is-on');
      document.body.classList.remove('zm-open');
    }
    const token = ++item.token;
    const finish = () => {
      if (token !== item.token) return;            // re-opened meanwhile
      item.details.classList.remove('is-closing', 'is-switching');
      item.details.open = false;
    };
    if (opts.immediate || reduce.matches) finish();
    else { item.details.classList.add('is-closing'); setTimeout(finish, OUT_MS); }
    if (opts.returnFocus) item.summary.focus();
  }

  function isOpen(item) { return item.details.open && !item.details.classList.contains('is-closing'); }

  function selectTab(item, tab, focus) {
    item.tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  }

  function wire(details) {
    if (details.__zm) return;
    const li = details.closest('li') || details;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    const item = { details, li, summary, token: 0, openTimer: 0, closeTimer: 0, viewTimer: 0,
                   tabs: [...details.querySelectorAll('[role="tab"]')] };
    details.__zm = item;
    items.push(item);

    const check = () => {
      if (!item.inside || isOpen(item)) return;
      const now = performance.now();
      const switching = !!(current && current !== item);
      if (now - item.anchor.t >= STILL_MS && now - item.enterAt >= (switching ? SWITCH_DELAY : OPEN_DELAY)) open(item, switching);
      else item.openTimer = setTimeout(check, 30);
    };
    li.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !desktop.matches) return;
      item.inside = true;
      clearTimeout(item.closeTimer);
      if (isOpen(item)) return;
      item.enterAt = performance.now();
      item.anchor = { x: e.clientX, y: e.clientY, t: item.enterAt };
      clearTimeout(item.openTimer);
      item.openTimer = setTimeout(check, 30);
    });
    li.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !item.anchor || isOpen(item)) return;
      if (Math.hypot(e.clientX - item.anchor.x, e.clientY - item.anchor.y) > STILL_PX) item.anchor = { x: e.clientX, y: e.clientY, t: performance.now() };
    }, { passive: true });
    li.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse' || !desktop.matches) return;
      item.inside = false;
      clearTimeout(item.openTimer);
      if (item.details.open) item.closeTimer = setTimeout(() => close(item), CLOSE_DELAY);
    });

    summary.addEventListener('click', (e) => {
      e.preventDefault();
      const mouse = e.pointerType ? e.pointerType === 'mouse' : (e.detail > 0 && finePointer.matches);   // keyboard: detail 0
      if (mouse && desktop.matches && summary.dataset.link) {
        window.location.href = summary.dataset.link;
        return;
      }
      isOpen(item) ? close(item) : open(item);
    });
    summary.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen(item)) open(item);
        const first = focusables(details.querySelector('.zm-panel') || details)[0];
        if (first) first.focus();
      }
    });
    details.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && item.details.open) {
        e.preventDefault(); e.stopPropagation();
        close(item, { immediate: true, returnFocus: true });
      }
    });
    li.addEventListener('focusout', (e) => {
      if (item.details.open && e.relatedTarget && !li.contains(e.relatedTarget)) close(item, { immediate: true });
    });

    item.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => selectTab(item, tab));
      tab.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(item.viewTimer);
        item.viewTimer = setTimeout(() => selectTab(item, tab), VIEW_DWELL);
      });
      tab.addEventListener('pointerleave', () => clearTimeout(item.viewTimer));
      tab.addEventListener('keydown', (e) => {
        const n = item.tabs.length;
        let j = null;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') j = (i + 1) % n;
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') j = (i - 1 + n) % n;
        if (e.key === 'Home') j = 0;
        if (e.key === 'End') j = n - 1;
        if (j !== null) { e.preventDefault(); selectTab(item, item.tabs[j], true); }
      });
    });

    if (window.Shopify && Shopify.designMode) {
      details.addEventListener('shopify:block:select', () => open(item));
      details.addEventListener('shopify:block:deselect', () => close(item, { immediate: true }));
    }
  }

  function init(root) {
    (root || document).querySelectorAll('details[data-zm]').forEach(wire);
  }

  document.addEventListener('click', (e) => {
    if (current && !current.li.contains(e.target)) close(current);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) close(current, { immediate: true, returnFocus: current.li.contains(document.activeElement) });
  });
  desktop.addEventListener('change', () => { if (current) close(current, { immediate: true }); });
  window.addEventListener('resize', () => { if (current) setTop(); }, { passive: true });
  document.addEventListener('shopify:section:load', (e) => { items = items.filter((it) => it.details.isConnected); init(e.target); });

  // Phone drawer: opening an accordion low on the screen must show what it opened. Measured 2026-10-17
  // (probe_drawer_feel.py): a group tapped at the drawer bottom opened with 4-6 px of 211-314 px of content visible
  // and no scroll - the chevron flips and nothing else happens. On open (never on close), scroll the drawer only as
  // far as the content needs, capped so the summary stays visible 8 px under the top. 'toggle' does not bubble, so
  // listen in the capture phase; works for the shop groups, "More styles" and the Guides/Company accordions alike.
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!d.open || !(d.classList.contains('zm-m__group') || d.classList.contains('zm-m__more'))) return;
    const scroller = d.closest('.drawer__scrollable');
    if (!scroller) return;
    requestAnimationFrame(() => {
      const cr = scroller.getBoundingClientRect();
      const dr = d.getBoundingClientRect();
      const overflow = dr.bottom - cr.bottom;
      if (overflow <= 0) return;
      const maxUp = Math.max(0, dr.top - cr.top - 8);
      const up = Math.min(overflow + 8, maxUp);
      if (up > 0) scroller.scrollBy({ top: up, behavior: reduce.matches ? 'instant' : 'smooth' });
    });
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
